import { createSlice } from '@reduxjs/toolkit';
import { Any } from 'react-spring';

interface Roadmap {
    roadmap_id: number;
    roadmap_name: string;
    created_at: Date;
    modified_at: Date;
}

interface RoadMapState {
    data: Roadmap[],
    loading: boolean,
    loadingMore: boolean,
    hasMore: boolean,
    page: number,
    perPage: number,
    totalPages: number,
    totalItems: number,
    searchTerm: string,
    error: null | object
    members?: string[]
    skills_required?: null | object
    duration?: string
    project_costs?: any
    project_cost?: string
    CPLI?: string
    start_date?: string
    end_date?: string
    durations?: any
    duration_with_working_days?: any
    duration_with_delay?: any
    members_data?: any
    is_sim_pending?: any
    sim_data: any;
    skill_capacity_percent?: any
    skill_members: any;
    roadmaps?: any
    roadmapsError?: any
    roadmapsLoading?: boolean
    saveRoadmapLoading?: boolean
    saveRoadmapError?: any
    saveRoadmapRes?: any
    singleRoadmapLoading: any,
    singleRoadmapRes: any,
    singleRoadmapError: any,
    sim_flag: boolean,
    integration_data: any;
    show_simulation: boolean,
    share_link: boolean,
    taskAllocated: any,
    org_holidays: any,
    stats: Object,
    status: string;
    child_entity_types: Record<string, number> | null;
    entity_type: string;
    progress: number;
    // cache last combined project-level API response (contains both live and sim_data)
    projectApiPayload?: any
    projectApiPayloadProjectId?: string | null
    resources_used_in_tasks?: any
}

const initialState: RoadMapState = {
    data: [],
    loading: false,
    loadingMore: false,
    hasMore: true,
    page: 1,
    perPage: 10,
    totalPages: 1,
    totalItems: 0,
    searchTerm: '',
    error: null,
    members: [],
    skills_required: {},
    integration_data: null,
    skill_members: {},
    duration: '',
    duration_with_working_days: '',
    duration_with_delay: '',
    project_cost: '',
    project_costs: '',
    org_holidays: [],
    CPLI: '',
    start_date: '',
    end_date: '',
    durations: [],
    sim_data: {},
    members_data: [],
    is_sim_pending: false,
    skill_capacity_percent: {},
    singleRoadmapLoading: false,
    singleRoadmapRes: [],
    singleRoadmapError: null,
    sim_flag: false,
    show_simulation: true,
    share_link: false,
    taskAllocated: {},
    stats: {},
    status: "",
    child_entity_types: {},
    entity_type: "Work Units",
    progress: 0,
    projectApiPayload: null,
    projectApiPayloadProjectId: null,
    resources_used_in_tasks: {},
}

export const roadmapSlice = createSlice({
    name: 'roadmap',
    initialState,
    reducers: {
        setRoadmap: (state, action) => {
            state.data = action.payload.Results
            state.skills_required = action.payload.skills_required
            state.skill_members = action.payload.skill_members
            state.duration = action.payload.duration
            state.duration_with_working_days = action.payload.duration_with_working_days
            state.duration_with_delay = action.payload.duration_with_delay
            state.project_cost = action.payload.project_cost
            state.project_costs = action.payload.project_costs
            state.CPLI = action.payload.CPLI
            state.start_date = action.payload.start_date
            state.end_date = action.payload.end_date
            state.durations = action.payload.durations
            state.members_data = action.payload.members_data
            state.is_sim_pending = action.payload.is_sim_pending
            state.skill_capacity_percent = action.payload.skill_capacity_percent
            state.show_simulation = action.payload.show_simulation
            state.share_link = action.payload.share_link
            state.taskAllocated = action.payload.taskAllocated
            state.org_holidays = action.payload.org_holidays
            state.stats = action.payload.stats
            state.status = action.payload.status
            state.child_entity_types = action.payload.child_entity_types
            state.entity_type = action.payload.entity_type
            state.progress = action.payload.progress
            state.integration_data = action.payload.integration_data
            state.sim_data = action.payload.sim_data
            state.resources_used_in_tasks = action.payload.resources_used_in_tasks
        },
        setProjectApiPayload: (state, action) => {
            if (action && action.payload && typeof action.payload === 'object' && Object.prototype.hasOwnProperty.call(action.payload, 'payload')) {
                state.projectApiPayload = action.payload.payload || null;
                state.projectApiPayloadProjectId = action.payload.projectId || null;
            } else {
                state.projectApiPayload = action.payload || null;
            }
        },
        resetRoadmap: (state) => {
            state.data = []
            state.skills_required = null
            state.skill_members = {}
            state.duration = ""
            state.duration_with_working_days = null
            state.duration_with_delay = null
            state.project_cost = ""
            state.project_costs = {}
            state.start_date = ""
            state.end_date = ""
            state.durations = []
            state.members_data = {}
            state.is_sim_pending = false
            state.skill_capacity_percent = {}
            state.show_simulation = false
            state.share_link = false
            state.taskAllocated = {}
            state.org_holidays = []
            state.stats = {}
            state.status = ""
            state.child_entity_types = {}
            state.entity_type = ""
            state.progress = 0
            state.projectApiPayload = null
            state.projectApiPayloadProjectId = null
            state.integration_data = null
            state.sim_data = {}
            state.resources_used_in_tasks = {}
        },
        setRoadmapCPLI: (state, action) => {
            state.skills_required = action.payload.skills_required
            state.duration = action.payload.duration
            state.duration_with_working_days = action.payload.duration_with_working_days
            state.duration_with_delay = action.payload.duration_with_delay
            state.project_cost = action.payload.project_cost
            state.project_costs = action.payload.project_costs
            state.CPLI = action.payload.CPLI
            state.start_date = action.payload.start_date
            state.end_date = action.payload.end_date
            state.durations = action.payload.durations
            state.members_data = action.payload.members_data
            state.is_sim_pending = action.payload.is_sim_pending
            state.skill_capacity_percent = action.payload.skill_capacity_percent
            state.org_holidays = action.payload.org_holidays
        },
        setRoadmapUpdate: (state, action) => {
            state.data = action.payload.Results
            state.duration = action.payload.duration
        },
        setProjectMembers: (state, action) => {
            state.members = action.payload
        },
        setRoadmapLoading: (state, action) => {
            state.loading = action.payload
        },
        setRoadmapError: (state, action) => {
            state.error = action.payload
            state.data = []
        },
        setRoadmaps: (state, action) => {
            state.roadmaps = action.payload?.roadmaps || []
            state.totalItems = action.payload?.total || 0
            state.totalPages = action.payload?.pages || 1
            state.page = action.payload?.page || 1
            state.perPage = action.payload?.per_page || 10
            state.hasMore = state.page < state.totalPages
        },
        appendRoadmaps: (state, action) => {
            state.roadmaps = [...state.roadmaps, ...(action.payload?.roadmaps || [])]
            state.totalItems = action.payload?.total || 0
            state.totalPages = action.payload?.pages || 1
            state.page = action.payload?.page || 1
            state.perPage = action.payload?.per_page || 10
            state.hasMore = state.page < state.totalPages
        },
        setRoadmapsLoading: (state, action) => {
            state.roadmapsLoading = action.payload
        },
        setRoadmapsLoadingMore: (state, action) => {
            state.loadingMore = action.payload
        },
        setRoadmapsError: (state, action) => {
            state.roadmapsError = action.payload
            state.data = []
        },
        resetRoadmapsPagination: (state) => {
            state.page = 1
            state.hasMore = true
        },
        setRoadmapSearchTerm: (state, action) => {
            state.searchTerm = action.payload
            state.page = 1
            state.hasMore = true
        },
        setSaveRoadmap: (state, action) => {
            state.saveRoadmapRes = action.payload
        },
        setSaveRoadmapLoading: (state, action) => {
            state.saveRoadmapLoading = action.payload
        },
        setSaveRoadmapError: (state, action) => {
            state.saveRoadmapError = action.payload
            state.saveRoadmapRes = []
        },
        setSingleRoadmap: (state, action) => {
            state.singleRoadmapRes = action.payload
            state.is_sim_pending = action.payload?.is_sim_pending ?? false
        },
        setSingleRoadmapLoading: (state, action) => {
            state.singleRoadmapLoading = action.payload
        },
        setSingleRoadmapError: (state, action) => {
            state.singleRoadmapError = action.payload
            state.saveRoadmapRes = []
        },
        setSimulationToggle: (state, action) => {
            state.sim_flag = action.payload
        }
    }
});

export const { setRoadmap, resetRoadmap, setRoadmapUpdate,
    setRoadmapLoading, setRoadmapError, setProjectMembers, setRoadmapCPLI,
    setRoadmapsLoading, setRoadmaps, setRoadmapsError, setSaveRoadmapLoading,
    setSaveRoadmapError, setSaveRoadmap, setSingleRoadmapLoading, setSingleRoadmap,
    setSingleRoadmapError, setSimulationToggle, setProjectApiPayload,
    appendRoadmaps, setRoadmapsLoadingMore, resetRoadmapsPagination, setRoadmapSearchTerm
} = roadmapSlice.actions;

export default roadmapSlice.reducer;

